import * as Utils from '../utils';

import { type IDomainTree } from './types';

const domainTree: IDomainTree = {
  name: 'application',
  loader: () =>
    System.import(/* webpackChunkName: "d_application" */ './application/index.js').then(
      module => module.default,
    ),
  cond: () => true,
  children: [
    {
      name: 'analytics',
      loader: () =>
        System.import(/* webpackChunkName: "d_analytics" */ './analytics/index.js').then(
          module => module.default,
        ),
      cond: () => true,
      children: [
        {
          name: 'stats',
          loader: () =>
            System.import(/* webpackChunkName: "d_stats" */ './analytics/stats/index.js').then(
              module => module.default,
            ),
          cond: () => !Utils.DebugSettings.shouldSkipStatsLogging(),
        },
        {
          name: 'comscore',
          loader: () =>
            System.import(/* webpackChunkName: "d_com" */ './analytics/comscore/index.js').then(
              module => module.default,
            ),
          cond: store => !Utils.TunerVars.getDisableThirdPartyTrackers(store),
        },
        {
          name: 'googleAnalytics',
          loader: () =>
            System.import(
              /* webpackChunkName: "d_pandaAn" */ './analytics/googleAnalytics/index.js',
            ).then(module => module.default),
          cond: store => !Utils.TunerVars.getDisableThirdPartyTrackers(store),
        },
        {
          name: 'googleTagManager',
          loader: () =>
            System.import(
              /* webpackChunkName: "d_googleTagManager" */ './analytics/googleTagManager/index.js',
            ).then(module => module.default),
          cond: store => !Utils.TunerVars.getDisableThirdPartyTrackers(store),
        },
        {
          name: 'krux',
          loader: () =>
            System.import(/* webpackChunkName: "d_krux" */ './analytics/krux/index.js').then(
              module => module.default,
            ),
          cond: store => !Utils.TunerVars.getDisableThirdPartyTrackers(store),
        },
        {
          name: 'branchio',
          loader: () =>
            System.import(
              /* webpackChunkName: "d_branchio" */ './analytics/branchio/index.js',
            ).then(module => module.default),
          cond: store => !Utils.TunerVars.getDisableThirdPartyTrackers(store),
        },
      ],
    },
    // @note: We need to load ads logic for T2 in order to initiate premium access
    {
      name: 'ads',
      loader: () =>
        System.import(/* webpackChunkName: "d_panda" */ './ads/index.js').then(
          module => module.default,
        ),
      cond: state => Utils.Listener.shouldCurrentListenerGetAds(state),
      children: [
        {
          name: 'mediaAds',
          loader: () =>
            System.import(/* webpackChunkName: "d_media-ads" */ './ads/mediaAds/index.tsx').then(
              module => module.default,
            ),
          cond: state =>
            Utils.Listener.shouldUseMediaAdsDomain(state) &&
            !Utils.Listener.isListenerAdFreeSkip(state),
        },
        {
          name: 'audioAds',
          loader: () =>
            System.import(/* webpackChunkName: "d_audio-panda" */ './ads/audioAds/index.js').then(
              module => module.default,
            ),
          cond: state =>
            !Utils.Listener.shouldUseMediaAdsDomain(state) &&
            !Utils.Listener.isListenerAdFreeSkip(state),
        },
        {
          name: 'displayAds',
          loader: () =>
            System.import(
              /* webpackChunkName: "d_display-panda" */ './ads/displayAds/index.js',
            ).then(module => module.default),
          cond: state => !Utils.Listener.isListenerAdFreeSkip(state),
        },
        {
          name: 'videoAds',
          loader: () =>
            System.import(/* webpackChunkName: "d_video-panda" */ './ads/videoAds/index.js').then(
              module => module.default,
            ),
          cond: () => true,
        },
        {
          name: 'rewards',
          loader: () =>
            System.import(/* webpackChunkName: "d_rewards" */ './ads/rewards/index.js').then(
              module => module.default,
            ),
          cond: () => true,
        },
      ],
    },
    {
      name: 'backstage',
      loader: () =>
        System.import(/* webpackChunkName: "d_backstage" */ './backstage/index.tsx').then(
          module => module.default,
        ),
      cond: () => true,
    },
    {
      name: 'catalog',
      loader: () =>
        System.import(/* webpackChunkName: "d_catalog" */ './catalog/index.js').then(
          module => module.default,
        ),
      cond: () => true,
    },
    {
      name: 'collection',
      loader: () =>
        System.import(/* webpackChunkName: "d_collection" */ './collection/index.tsx').then(
          module => module.default,
        ),
      cond: state => Utils.Listener.isLoggedIn(state),
    },
    {
      name: 'debug',
      loader: () =>
        System.import(/* webpackChunkName: "d_debug" */ './debug/index.js').then(
          module => module.default,
        ),
      cond: Utils.DebugSettings.isDebugMenuEnabled,
    },
    {
      name: 'discovery',
      loader: () =>
        System.import(/* webpackChunkName: "d_discovery" */ './discovery/index.js').then(
          module => module.default,
        ),
      cond: () => true,
    },
    {
      name: 'listener',
      loader: () =>
        System.import(/* webpackChunkName: "d_listener" */ './listener/index.js').then(
          module => module.default,
        ),
      cond: () => true,
      children: [
        {
          name: 'education',
          loader: () =>
            System.import(
              /* webpackChunkName: "d_education" */ './listener/education/index.js',
            ).then(module => module.default),
          cond: () => true,
        },
        {
          name: 'desktopApp',
          loader: () =>
            System.import(
              /* webpackChunkName: "d_desktopApp" */ './listener/desktopApp/index.js',
            ).then(module => module.default),
          cond: () => true,
        },
      ],
    },
    {
      name: 'payments',
      loader: () =>
        System.import(/* webpackChunkName: "d_payments" */ './payments/index.js').then(
          module => module.default,
        ),
      cond: () => true,
      children: [
        {
          name: 'specialOffers',
          loader: () =>
            System.import(
              /* webpackChunkName: "d_specialOffers" */ './payments/specialOffers/index.js',
            ).then(module => module.default),
          cond: (state, isStarted) =>
            !Utils.TunerVars.isOnDemandSpecialOffersEnabled(state) || !!isStarted,
        },
        {
          name: 'verification',
          loader: () =>
            System.import(
              /* webpackChunkName: "d_verification" */ './payments/verification/index.js',
            ).then(module => module.default),
          cond: () => true,
        },
        {
          name: 'familyPlanAcceptInvite',
          loader: () =>
            System.import(
              /* webpackChunkName: "d_familyPlanAcceptInvite" */ './payments/familyPlanAcceptInvite/index.js',
            ).then(module => module.default),
          cond: (state, isStarted) =>
            !Utils.TunerVars.isOnDemandFamilyPlanAcceptInviteEnabled(state) || !!isStarted,
        },
      ],
    },
    {
      name: 'playback',
      loader: () =>
        System.import(/* webpackChunkName: "d_playback" */ './playback/index.js').then(
          module => module.default,
        ),
      cond: () => true,
      children: [
        {
          name: 'aps',
          loader: () =>
            System.import(/* webpackChunkName: "d_aps" */ './playback/aps/index.js').then(
              module => module.default,
            ),
          cond: Utils.Capabilities.isPlaybackEnabled,
        },
        {
          name: 'audio',
          loader: () =>
            System.import(/* webpackChunkName: "d_audio" */ './playback/audio/index.js').then(
              module => module.default,
            ),
          cond: Utils.Capabilities.isPlaybackEnabled,
          children: [
            {
              name: 'sample',
              loader: () =>
                System.import(
                  /* webpackChunkName: "d_sample" */ './playback/audio/sample/index.js',
                ).then(module => module.default),
              cond: () => true,
            },
            {
              name: 'volume',
              loader: () =>
                System.import(
                  /* webpackChunkName: "d_volume" */ './playback/audio/volume/index.js',
                ).then(module => module.default),
              cond: () => true,
            },
          ],
        },
        {
          name: 'stations',
          loader: () =>
            System.import(/* webpackChunkName: "d_station" */ './playback/station/index.js').then(
              module => module.default,
            ),
          cond: Utils.Capabilities.isPlaybackEnabled,
        },
        {
          name: 'podcast',
          loader: () =>
            System.import(/* webpackChunkName: "d_podcast" */ './playback/podcast/index.js').then(
              module => module.default,
            ),
          cond: state => Utils.Capabilities.isPlaybackEnabled(state),
        },
        {
          name: 'playlist',
          loader: () =>
            System.import(/* webpackChunkName: "d_playlist" */ './playback/playlist/index.js').then(
              module => module.default,
            ),
          cond: Utils.Capabilities.isPlaybackEnabled,
        },
        {
          name: 'crosstab',
          loader: () =>
            System.import(/* webpackChunkName: "d_crosstab" */ './playback/crosstab/index.js').then(
              module => module.default,
            ),
          cond: Utils.Capabilities.isPlaybackEnabled,
        },
        {
          name: 'simstream',
          loader: () =>
            System.import(
              /* webpackChunkName: "d_simstream" */ './playback/simstream/index.js',
            ).then(module => module.default),
          cond: Utils.Capabilities.isPlaybackEnabled,
        },
        {
          name: 'playlistT3',
          loader: () =>
            System.import(
              /* webpackChunkName: "d_playlistT3" */ './playback/playlistT3/index.js',
            ).then(module => module.default),
          cond: () => true,
        },
        {
          name: 'drm',
          loader: () =>
            System.import(/* webpackChunkName: "d_drm" */ './playback/drm/index.js').then(
              module => module.default,
            ),
          cond: Utils.Capabilities.isPlaybackEnabled,
        },
        {
          name: 'video',
          loader: () =>
            System.import(/* webpackChunkName: "d_video" */ './playback/video/index.tsx').then(
              module => module.default,
            ),
          cond: state => Utils.Capabilities.isPlaybackEnabled(state),
        },
      ],
    },
    {
      name: 'searchT3',
      loader: () =>
        System.import(/* webpackChunkName: "d_search-t3" */ './searchT3/index.tsx').then(
          module => module.default,
        ),
      cond: Utils.Capabilities.isPlaybackEnabled,
    },
    {
      name: 'smartconversion',
      loader: () =>
        System.import(
          /* webpackChunkName: "d_smartconversion" */ './smartconversion/index.ts',
        ).then(module => module.default),
      cond: Utils.Listener.shouldCurrentListenerGetAds,
    },
  ],
};

export default domainTree;
